<script>
    import { getContext } from 'svelte';
    import { path } from 'd3-path';
    import { select } from 'd3-selection';
    import { fade } from 'svelte/transition';

    // --- Properties
    export let dataKey;
    export let transactionKey;
    export let weekday;
    export let dayNr;

    let width;
    let height;
    let carAxisG;

    // --- Contexts
    const {
        selectedCars,
        cars,
        carStops,
        poiTypeDomain,
        selectedMatches,
        pointsOfInterest,
        selectedTransactions,
        focus,
        setPOIFocus,
        activePOI,
        isFocusPOI,
        editMatch,
        focusSelected,
    } = getContext(dataKey);
    const { timeScale, carScale, ccScale, stopScale, carAxis } =
        getContext(transactionKey);
    $: stops = $selectedCars.reduce((map, carID) => {
        const stops = [];
        $cars[carID].stops.forEach((key) => {
            const s = $carStops[key];
            if (s.start.day <= dayNr && s.stop.day >= dayNr) {
                const poi = $pointsOfInterest[`${s.lat}_${s.long}`];
                const type = poiTypeDomain.includes(poi.type)
                    ? poi.type
                    : 'Other';
                stops.push({ ...s, type, poi });
            }
        });
        map[carID] = stops;
        return map;
    }, {});
    $: rejected_stops = $selectedCars.reduce((map, carID) => {
        const stops = [];
        ($cars[carID].rejected_stops || []).forEach((key) => {
            const s = $carStops[key];
            if (s.start.day <= dayNr && s.stop.day >= dayNr) {
                stops.push(s);
            }
        });
        map[carID] = stops;
        return map;
    }, {});
    $: matches = $selectedMatches.filter((m) => m.t.day == dayNr);
    $: transactions = $selectedTransactions
        .filter((t) => t.day == dayNr)
        .filter((t) => matches.filter((m) => m.t._key == t._key).length == 0);

    $: doubles = findDoubles(matches);

    function findDoubles(matches) {
        const doubles = [];
        for (let i = 0; i < matches.length - 1; i++) {
            const m_i = matches[i];
            for (let j = i + 1; j < matches.length; j++) {
                const m_j = matches[j];
                if (m_i.s != m_j.s && m_i.t._key == m_j.t._key) {
                    doubles.push([m_i, m_j]);
                }
            }
        }
        return doubles;
    }

    // --- Draw axes
    $: updateAxis($selectedCars);

    function updateAxis(_) {
        if (carAxisG) {
            select(carAxisG)
                .call(carAxis)
                .call((g) => g.select('.domain').remove());
        }
    }

    // --- Draw transactions and stops
    function transactionColor(ccnum) {
        if (ccnum == 0) {
            return 'black';
        }
        return ccScale(ccnum);
    }

    function stopStart(s) {
        if (s.start.day < dayNr) {
            return timeScale(0);
        }
        return timeScale(s.start.time_of_day);
    }

    function stopWidth(s) {
        if (s.stop.day > dayNr) {
            return timeScale(23 * 3600 + 59 * 60 + 59) - stopStart(s);
        }
        return timeScale(s.stop.time_of_day) - stopStart(s);
    }

    function stopOpacity(stop, focus, active) {
        if (focus.poi) {
            return isFocusPOI(focus, stop.poi._key) ? 1 : 0.2;
        }
        return active[stop.poi._key] ? 0.5 : 0.2;
    }

    function dMatchPath(dMatch) {
        const p = path();
        const h = ((height - 25) / $selectedCars.length - 4) / 2;
        const x1 = timeScale(dMatch[0].t.time_of_day);
        const x2 = timeScale(dMatch[1].t.time_of_day);
        const y1 = carScale($carStops[dMatch[0].s].car) + h;
        const y2 = carScale($carStops[dMatch[1].s].car) + h;
        p.moveTo(x1, y1);
        p.lineTo(x2, y2);
        return p.toString();
    }

    // --- selected
    function clickSVG(e) {
        if (!e.target.matches('rect')) {
            $focusSelected = false;
            setPOIFocus(null);
        }
    }

    function clickStop(stop) {
        setPOIFocus(stop.poi);
        $focusSelected = true;
    }

    // --- Tooltip
    let svg;
    let tooltipCoords = [0, 0];
    let tooltipStop = null;
    let tooltipTransaction = null;
    let tooltipMatch = null;
    let lastStopTooltip = null;
    let lastTransactionTooltip = null;
    let lastMatchTooltip = null;

    function startTransactionTooltip(transaction) {
        const coords = [timeScale(transaction.time_of_day), 0];
        // console.log('start transaction tooltip');
        tooltipCoords = computeTooltipPos(coords);
        tooltipCoords[1] -= 33;
        tooltipTransaction = transaction;
        lastTransactionTooltip = transaction;
    }

    function startStopTooltip(stop) {
        const coords = [
            stopStart(stop) + stopWidth(stop) / 2,
            carScale(stop.car),
        ];
        // console.log('start stop tooltip');
        tooltipCoords = computeTooltipPos(coords);
        tooltipCoords[1] -= 25;
        if (!$focusSelected) {
            setPOIFocus(stop.poi);
        }
        tooltipStop = stop;
        lastStopTooltip = stop;
    }

    function startMatchTooltip(match) {
        // get the stop
        // get the transaction
        const coords = [
            timeScale(match.t.time_of_day) - 5,
            carScale($carStops[match.s].car),
        ];
        // console.log('start match tooltip');
        tooltipCoords = computeTooltipPos(coords);
        tooltipCoords[1] -= 15;
        if (!$focusSelected) {
            setPOIFocus(stop.poi);
        }
        tooltipMatch = match;
        lastMatchTooltip = match;
    }

    function stopTransactionTooltip(event) {
        // console.log('stop transaction tooltip');
        tooltipTransaction = null;
    }

    function stopStopTooltip(event) {
        // console.log('stop stop tooltip');
        if (!$focusSelected) {
            setPOIFocus(null);
        }
        tooltipStop = null;
    }

    function stopMatchTooltip(event) {
        // console.log('stop match tooltip');
        if (!$focusSelected) {
            setPOIFocus(null);
        }
        tooltipMatch = null;
    }

    function mouseEnterMatchTooltip() {
        // console.log('enter match tooltip');
        if (!lastMatchTooltip) {
            return;
        }
        const coords = [
            timeScale(lastMatchTooltip.t.time_of_day) - 5,
            carScale(lastMatchTooltip.s.car),
        ];
        // console.log('start match tooltip');
        tooltipCoords = computeTooltipPos(coords);
        tooltipCoords[1] -= 33;
        tooltipMatch = lastMatchTooltip;
        if (!$focusSelected) {
            setPOIFocus(lastMatchTooltip.poi);
        }
    }

    function mouseLeaveMatchTooltip() {
        // console.log('leave match tooltip');
        lastMatchTooltip = null;
        if (!$focusSelected) {
            setPOIFocus(null);
        }
        tooltipMatch = null;
    }

    function mouseEnterStopTooltip() {
        if (!lastStopTooltip) {
            return;
        }
        // console.log('enter stop tooltip');
        const coords = [
            stopStart(lastStopTooltip) + stopWidth(lastStopTooltip) / 2,
            carScale(lastStopTooltip.car),
        ];
        // console.log('start stop tooltip');
        tooltipCoords = computeTooltipPos(coords);
        tooltipCoords[1] -= 33;
        tooltipStop = lastStopTooltip;
        if (!$focusSelected) {
            setPOIFocus(lastStopTooltip.poi);
        }
    }

    function mouseLeaveStopTooltip() {
        // console.log('leave stop tooltip');
        lastStopTooltip = null;
        tooltipStop = null;
        if (!$focusSelected) {
            setPOIFocus(null);
        }
    }

    function mouseEnterTransactionTooltip() {
        if (!lastTransactionTooltip) {
            return;
        }
        // console.log('enter transaction tooltip');
        const coords = [timeScale(lastTransactionTooltip.time_of_day), 0];
        // console.log('start transaction tooltip');
        tooltipCoords = computeTooltipPos(coords);
        tooltipCoords[1] -= 33;
        tooltipTransaction = lastTransactionTooltip;
    }

    function mouseLeaveTransactionTooltip() {
        // console.log('leave transaction tooltip');
        lastTransactionTooltip = null;
        tooltipTransaction = null;
    }

    function computeTooltipPos(coords) {
        const matrix = svg.getScreenCTM();
        const rect = svg.getBoundingClientRect();
        const p = svg.createSVGPoint();
        p.x = coords[0];
        p.y = coords[1];
        const screen = p.matrixTransform(matrix);
        return [screen.x - rect.x + 5, screen.y - rect.y - 88];
    }
</script>

<div class="day col" bind:clientWidth={width} bind:clientHeight={height}>
    <span class="dayname">{weekday} January {dayNr}</span>
    <svg bind:this={svg} {width} {height} on:click={clickSVG}>
        <g class="missing-transactions">
            {#each transactions as t}
                <circle
                    cx={timeScale(t.time_of_day)}
                    cy={15}
                    fill={transactionColor(t.used_cc)}
                    r="3"
                    on:mouseover={() => startTransactionTooltip(t)}
                    on:mouseleave={stopTransactionTooltip}
                />
            {/each}
        </g>
        <g transform="translate(0, 20)">
            {#each Object.keys(stops) as car}
                {#each stops[car].filter((s) => s.state === 'accepted') as s}
                    <rect
                        x={stopStart(s)}
                        y={carScale(car)}
                        fill={stopScale(s.type)}
                        width={stopWidth(s)}
                        height={(height - 25) / $selectedCars.length - 4}
                        stroke-width="0"
                        opacity={stopOpacity(s, $focus, $activePOI)}
                        on:mouseover={() => startStopTooltip(s)}
                        on:mouseleave={stopStopTooltip}
                        on:click={() => clickStop(s)}
                    />
                {/each}
                {#each rejected_stops[car] as s}
                    <rect
                        x={stopStart(s)}
                        y={carScale(car)}
                        fill="red"
                        width={stopWidth(s)}
                        height={(height - 25) / $selectedCars.length - 4}
                        stroke-width="0"
                        opacity="0.2"
                    />
                {/each}
            {/each}
            {#each doubles as d}
                <!-- svelte-ignore component-name-lowercase -->
                <path
                    class="path"
                    d={dMatchPath(d)}
                    stroke="currentColor"
                    fill="transparent"
                />
            {/each}
            {#each matches as m}
                <!-- {#if $selectedCars.includes(`${$carStops[m.s].car}`)} -->
                <circle
                    cx={timeScale(m.t.time_of_day)}
                    cy={carScale($carStops[m.s].car) +
                        ((height - 25) / $selectedCars.length - 4) / 2}
                    fill="{transactionColor(m.t.used_cc)}cd"
                    r="4"
                    on:mouseenter={() => startMatchTooltip(m)}
                    on:mouseleave={stopMatchTooltip}
                />
                <!-- {/if} -->
            {/each}
            <g
                class="ccaxis"
                bind:this={carAxisG}
                transform="translate(35, 0)"
            />
        </g>
    </svg>
    {#if tooltipTransaction}
        <div
            transition:fade={{ duration: 50 }}
            class="tooltip col"
            style="left:{tooltipCoords[0]}px;top:{tooltipCoords[1]}px"
            on:mouseenter={mouseEnterTransactionTooltip}
            on:mouseleave={mouseLeaveTransactionTooltip}
        >
            <span><b>Location</b>: {tooltipTransaction.location}</span>
            <span><b>Time</b>: {tooltipTransaction.display_time}</span>
            <span><b>Price</b>: {tooltipTransaction.price}</span>
            <span
                ><b>Discount price</b>: {tooltipTransaction.discount_price}</span
            >
            <span><b>CC</b>: {tooltipTransaction.used_cc}</span>
            <span><b>Loyalty</b>: {tooltipTransaction.used_ly}</span>
            <span><b>Matched CCs</b>: {tooltipTransaction.matched_ccnums}</span>
            <span><b>Matched LYs</b>: {tooltipTransaction.matched_lynums}</span>
        </div>
    {/if}
    {#if tooltipStop}
        <div
            transition:fade={{ duration: 50 }}
            class="tooltip col"
            style="left:{tooltipCoords[0]}px;top:{tooltipCoords[1]}px"
            on:mouseenter={mouseEnterStopTooltip}
            on:mouseleave={mouseLeaveStopTooltip}
        >
            <span><b>Location</b>:{tooltipStop.poi.locations}</span>
            <span><b>POI Type</b>: {tooltipStop.type}</span>
            <span><b>Arrival</b>: {tooltipStop.start.display_time}</span>
            <span><b>Departure</b>: {tooltipStop.stop.display_time}</span>
            <span><b>Duration</b>: {tooltipStop.duration.toFixed(0)}</span>
            <span><b>Avg. Speed</b>: {tooltipStop.speed.toFixed(3)}</span>
            <span><b>Distance</b>: {tooltipStop.distance.toFixed(0)}</span>
            <span><b>Car</b>: {tooltipStop.car}</span>
        </div>
    {/if}
    {#if tooltipMatch}
        <div
            transition:fade={{ duration: 50 }}
            class="tooltip col"
            style="left:{tooltipCoords[0]}px;top:{tooltipCoords[1]}px"
            on:mouseenter={mouseEnterMatchTooltip}
            on:mouseleave={mouseLeaveMatchTooltip}
        >
            <span><b>Location</b>: {tooltipMatch.t.location}</span>
            <span
                ><b>Arrival</b>: {$carStops[tooltipMatch.s].start
                    .display_time}</span
            >
            <span><b>Transaction</b>: {tooltipMatch.t.display_time}</span>
            <span
                ><b>Departure</b>: {$carStops[tooltipMatch.s].stop
                    .display_time}</span
            >
            <span><b>Price</b>: {tooltipMatch.t.price}</span>
            <span><b>CC</b>: {tooltipMatch.t.used_cc}</span>
            <span><b>Car</b>: {$carStops[tooltipMatch.s].car}</span>
            <div class="row">
                <button on:click={() => editMatch(tooltipMatch)}
                    >{$carStops[tooltipMatch.s].transactions.filter((tc) => {
                        return tc.t._key === tooltipMatch.t._key;
                    }).length > 0
                        ? 'unmatch'
                        : 'match'}</button
                >
            </div>
        </div>
    {/if}
</div>

<style>
    .day {
        height: calc((100% - 15px) / 7);
        vertical-align: center;
        flex-grow: 0;
        flex-shrink: 0;
    }
    .dayname {
        font-size: 12px;
        position: absolute;
        top: 0px;
        left: 2px;
    }
    svg {
        position: absolute;
        left: 0;
    }
    .tooltip {
        border: 1px solid #ddd;
        box-shadow: 1px 1px 1px #ddd;
        background: white;
        border-radius: 4px;
        position: relative;
        font-size: 12px;
        width: 220px;
        z-index: 1;
        padding: 5px;
    }
</style>
